import * as React from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  CheckCircle2,
  Circle,
  Clock,
  ExternalLink,
  Eye,
  EyeOff,
  History,
  Pencil,
  Play,
  Plus,
  Presentation as PresentationIcon,
  Radio,
  Trash2,
  Users,
} from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import { useQuery } from '@/hooks/use-query'
import { usePoll } from '@/hooks/use-realtime'
import { formatDateTime } from '@/lib/format'
import type {
  Activity,
  ActivityAttendance,
  ActivityCompletion,
  ActivityResource,
  LivePresentationSession,
  Presentation,
  PresentationSession,
  Profile,
} from '@/lib/types'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ErrorNote, Loading, Markdown, Page } from '@/components/page'
import { ActivityEditor } from '@/pages/activity-editor'
import { KIND_META, statusVariant } from '@/pages/activities'
import { PresentStartDialog } from '@/pages/present-start'
import { useDirty, useDiscardGuard } from '@/hooks/use-unsaved'
import { DiscardPrompt } from '@/components/discard-prompt'

export function ActivityDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { session, isLead, isCoach } = useAuth()
  const userId = session?.user?.id
  const [editing, setEditing] = React.useState(false)
  const [addingResource, setAddingResource] = React.useState(false)
  const [starting, setStarting] = React.useState(false)

  const { data, loading, error, refetch } = useQuery(async () => {
    if (!id) return null
    const [act, res, comps, attend, sessions] = await Promise.all([
      sb().from('activities').select('*').eq('id', id).maybeSingle(),
      sb().from('activity_resources').select('*').eq('activity_id', id).order('display_order'),
      sb().from('activity_completions').select('*').eq('activity_id', id),
      // The per-person roll-up view from 0012 — one row per person who has sat
      // through this deck, however many times they've done it.
      sb().from('activity_attendance').select('*').eq('activity_id', id),
      sb()
        .from('presentation_sessions')
        .select('*')
        .eq('activity_id', id)
        .order('started_at', { ascending: false }),
    ])
    if (act.error) throw act.error
    if (res.error) throw res.error
    if (comps.error) throw comps.error
    if (attend.error) throw attend.error
    if (sessions.error) throw sessions.error
    if (!act.data) return null

    const activity = act.data as Activity
    const completions = (comps.data ?? []) as ActivityCompletion[]
    const sessionRows = (sessions.data ?? []) as PresentationSession[]

    // Headcount per session. The activity_attendance view rolls up ACROSS
    // sessions, so it can't answer "how many were in this one" — that needs the
    // raw attendee rows.
    const perSession = new Map<string, number>()
    if (sessionRows.length > 0) {
      const { data: rows, error: attErr } = await sb()
        .from('presentation_attendees')
        .select('session_id')
        .in('session_id', sessionRows.map((s) => s.id))
      if (attErr) throw attErr
      for (const r of (rows ?? []) as { session_id: string }[]) {
        perSession.set(r.session_id, (perSession.get(r.session_id) ?? 0) + 1)
      }
    }

    // The deck behind this activity, when there is one.
    let deck: Presentation | null = null
    if (activity.presentation_id) {
      const { data: p, error: pErr } = await sb()
        .from('presentations')
        .select('*')
        .eq('id', activity.presentation_id)
        .maybeSingle()
      if (pErr) throw pErr
      deck = (p ?? null) as Presentation | null
    }

    // Names for the "who's done it" list. Profile reads are gated by RLS to
    // teammates, so this can only ever return people on this team.
    let profiles: Profile[] = []
    if (completions.length > 0) {
      const { data: p, error: perr } = await sb()
        .from('profiles')
        .select('*')
        .in('user_id', completions.map((c) => c.user_id))
      if (perr) throw perr
      profiles = (p ?? []) as Profile[]
    }
    return {
      activity,
      deck,
      resources: (res.data ?? []) as ActivityResource[],
      completions,
      attendance: (attend.data ?? []) as ActivityAttendance[],
      sessions: sessionRows,
      perSession,
      profiles,
    }
  }, [id])

  /**
   * Is the room in this deck right now?
   *
   * Its own query rather than a field on the one above, because it has a
   * different refresh rate and a different job: everything else on this page is
   * a record of what happened, and this is an invitation that has to show up
   * within seconds of someone else hitting Present. The view (0020) already
   * filters out sessions whose presenter closed their tab without exiting.
   *
   * Ten seconds because the cost of being late here is small — a teammate walks
   * in, the button appears — and the cost of a tighter poll is every member of
   * every team hitting PostgREST on a shared instance that already answers 503
   * when it runs out of connections.
   */
  const { data: liveSession, refetch: refetchLive } = useQuery(async () => {
    if (!id) return null
    const { data: rows, error: err } = await sb()
      .from('live_presentation_sessions')
      .select('*')
      .eq('activity_id', id)
      .order('started_at', { ascending: false })
      .limit(1)
    if (err) throw err
    const row = ((rows ?? []) as LivePresentationSession[])[0] ?? null
    if (!row?.presenter_id) return row ? { ...row, presenter: null } : null

    // Resolved here rather than through the page's `nameFor`, which only knows
    // people who have a completion row — the presenter of a session that is
    // running right now frequently doesn't yet, and "Live now · Someone" is a
    // worse invitation than no name at all. One extra round trip, and only while
    // a session is actually live.
    const { data: p } = await sb()
      .from('profiles')
      .select('display_name, email')
      .eq('user_id', row.presenter_id)
      .maybeSingle()
    const prof = p as Pick<Profile, 'display_name' | 'email'> | null
    return { ...row, presenter: prof?.display_name || prof?.email || null }
  }, [id])
  usePoll(() => void refetchLive(), 10_000, Boolean(id))

  // Distinguishes "join the room" from "get back to the deck you're running" —
  // a presenter who clicked away needs their own session back, not to become a
  // follower of themselves.
  const iAmPresenting = Boolean(liveSession && userId && liveSession.presenter_id === userId)

  function joinLive() {
    if (!liveSession || !id) return
    navigate(`/activities/${id}/present?session=${liveSession.id}`)
  }

  async function toggle() {
    if (!data || !userId) return
    const done = data.completions.some((c) => c.user_id === userId)
    if (done) {
      await sb().from('activity_completions').delete().eq('activity_id', data.activity.id).eq('user_id', userId)
    } else {
      await sb()
        .from('activity_completions')
        .insert({ activity_id: data.activity.id, team_id: data.activity.team_id })
    }
    void refetch()
  }

  /**
   * Publish or unpublish. Coach-only, and the database agrees — a lead's PATCH of
   * this column is refused by RLS, not merely hidden from them here.
   */
  async function setVisible(next: boolean) {
    if (!data) return
    const { error: err } = await sb()
      .from('activities')
      .update({ visible: next })
      .eq('id', data.activity.id)
    if (err) {
      window.alert(err.message)
      return
    }
    void refetch()
  }

  async function removeActivity() {
    if (!data) return
    if (!window.confirm(`Delete “${data.activity.title}”? This also removes its resources and completion records.`)) return
    const { error: err } = await sb().from('activities').delete().eq('id', data.activity.id)
    if (err) {
      window.alert(err.message)
      return
    }
    navigate('/activities')
  }

  if (loading && !data) {
    return (
      <Page title="Topic">
        <Loading />
      </Page>
    )
  }
  if (error) {
    return (
      <Page title="Topic">
        <ErrorNote error={error} />
      </Page>
    )
  }
  if (!data) {
    return (
      <Page title="Topic not found">
        <Button variant="outline" asChild>
          <Link to="/activities">
            <ArrowLeft /> Back to topics
          </Link>
        </Button>
      </Page>
    )
  }

  const { activity, deck, resources, completions, attendance, sessions, perSession, profiles } = data
  const meta = KIND_META[activity.kind]
  const done = completions.some((c) => c.user_id === userId)
  const nameFor = (uid: string) => {
    const p = profiles.find((x) => x.user_id === uid)
    return p?.display_name || p?.email || 'Someone'
  }

  // "Been through it" merges the two ways that can happen: sitting through a
  // session (attendance, recorded by the presenter) and ticking the box
  // yourself. Attendance writes a completion row too, so the same person would
  // otherwise appear twice — attendance wins, since it's the stronger claim and
  // carries the session timestamp.
  const attendedUserIds = new Set(attendance.map((a) => a.user_id).filter(Boolean) as string[])
  const beenThrough = [
    ...attendance.map((a) => ({
      key: a.member_id,
      name: a.person,
      when: a.last_attended_at,
      times: a.times_attended,
      attended: true,
    })),
    ...completions
      .filter((c) => !attendedUserIds.has(c.user_id))
      .map((c) => ({
        key: c.id,
        name: nameFor(c.user_id),
        when: c.completed_at,
        times: 1,
        attended: false,
      })),
  ].sort((a, b) => b.when.localeCompare(a.when))

  return (
    <Page
      title={activity.title}
      description={activity.summary ?? undefined}
      actions={
        <>
          {/* Ahead of Present on purpose. Someone arriving at this page while the
              room is mid-deck almost always means to join it, and the two
              buttons side by side is exactly the moment to make starting a
              SECOND concurrent session the less obvious click. */}
          {liveSession && (
            <Button onClick={joinLive}>
              <Radio className="text-emerald-300" />
              {iAmPresenting ? 'Back to your deck' : 'Join live'}
            </Button>
          )}
          {deck && (
            <Button variant={liveSession ? 'outline' : 'default'} onClick={() => setStarting(true)}>
              <Play /> Present
            </Button>
          )}
          <Button variant={done ? 'outline' : 'default'} onClick={() => void toggle()}>
            {done ? <CheckCircle2 /> : <Circle />}
            {done ? 'Completed' : 'Mark complete'}
          </Button>
          {/* Presenting and ticking the box are for everyone; authoring the
              topic is not. RLS refuses the write either way (0015) — hiding the
              controls keeps members from meeting a 403 they can't act on. */}
          {isCoach && (
            <Button
              variant="outline"
              onClick={() => void setVisible(!activity.visible)}
              title={
                activity.visible
                  ? 'Hide this topic from the team'
                  : 'Make this topic visible to the team'
              }
            >
              {activity.visible ? <EyeOff /> : <Eye />}
              {activity.visible ? 'Hide' : 'Publish'}
            </Button>
          )}
          {isLead && (
            <>
              <Button variant="outline" onClick={() => setEditing(true)}>
                <Pencil /> Edit
              </Button>
              <Button variant="ghost" onClick={() => void removeActivity()} aria-label="Delete topic">
                <Trash2 />
              </Button>
            </>
          )}
        </>
      }
    >
      <Button variant="ghost" size="sm" className="mb-4 -ml-2" asChild>
        <Link to="/activities">
          <ArrowLeft /> Topics
        </Link>
      </Button>

      {/* Seeing this page with visible=false means you're a coach, so the notice
          is about the team's view rather than your own. */}
      {!activity.visible && (
        <div className="mb-4 flex items-start gap-2.5 rounded-md border border-dashed bg-muted/40 p-3 text-sm">
          <EyeOff className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <p className="text-muted-foreground">
            <span className="font-medium text-foreground">Not visible to the team.</span> Members
            and the team lead can’t see this topic yet. Presenting it publishes it, or use{' '}
            <span className="font-medium text-foreground">Publish</span> above.
          </p>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          {deck && (
            <Card className="border-primary/30 bg-primary/[0.03]">
              <CardContent className="flex flex-wrap items-center gap-4 p-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                  <PresentationIcon className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{deck.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {deck.slide_count ? `${deck.slide_count} slides` : 'Slide deck'}
                    {sessions.length > 0 &&
                      ` · run ${sessions.length} ${sessions.length === 1 ? 'time' : 'times'}`}
                  </p>
                  {/* Says where the room actually is, so joining is an informed
                      click rather than a leap — and so a stale session that the
                      view hasn't aged out yet is obvious from the slide number
                      not moving. */}
                  {liveSession && (
                    <p className="mt-1 flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" />
                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                      </span>
                      Live now
                      {liveSession.presenter && ` · ${liveSession.presenter}`}
                      {liveSession.current_slide &&
                        ` · slide ${liveSession.current_slide}${
                          deck.slide_count ? ` of ${deck.slide_count}` : ''
                        }`}
                    </p>
                  )}
                </div>
                {liveSession && (
                  <Button onClick={joinLive}>
                    <Radio className="text-emerald-300" />
                    {iAmPresenting ? 'Back to your deck' : 'Join live'}
                  </Button>
                )}
                <Button
                  variant={liveSession ? 'outline' : 'default'}
                  onClick={() => setStarting(true)}
                >
                  <Play /> Present
                </Button>
              </CardContent>
            </Card>
          )}

          {activity.content && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Session notes</CardTitle>
              </CardHeader>
              <CardContent>
                <Markdown source={activity.content} />
              </CardContent>
            </Card>
          )}

          {activity.exercise && (
            <Card className="border-primary/30">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">The activity</CardTitle>
              </CardHeader>
              <CardContent>
                <Markdown source={activity.exercise} />
              </CardContent>
            </Card>
          )}

          {!activity.content && !activity.exercise && (
            <Card className="border-dashed shadow-none">
              <CardContent className="py-10 text-center text-sm text-muted-foreground">
                No notes yet.{' '}
                {isLead && (
                  <>
                    <button className="font-medium text-primary hover:underline" onClick={() => setEditing(true)}>
                      Add some
                    </button>
                    .
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-5">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2.5 text-sm">
              <Row label="Type">
                <Badge variant="muted" className="gap-1 text-[10px]">
                  <meta.icon className="h-3 w-3" /> {meta.label}
                </Badge>
              </Row>
              <Row label="Status">
                <Badge variant={statusVariant(activity.status)} className="text-[10px]">
                  {activity.status === 'in_progress' ? 'in progress' : activity.status}
                </Badge>
              </Row>
              {/* Only shown to coaches, because for anyone else it could only
                  ever read "Visible" — they cannot load a hidden topic. */}
              {isCoach && (
                <Row label="Visibility">
                  <Badge variant={activity.visible ? 'success' : 'outline'} className="gap-1 text-[10px]">
                    {activity.visible ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                    {activity.visible ? 'Team' : 'DPL only'}
                  </Badge>
                </Row>
              )}
              {activity.presenter && <Row label="Led by">{activity.presenter}</Row>}
              {activity.scheduled_for && (
                <Row label="When">
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                    {formatDateTime(activity.scheduled_for)}
                  </span>
                </Row>
              )}
              {activity.duration_minutes && <Row label="Length">{activity.duration_minutes} min</Row>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-base">Resources</CardTitle>
              {isLead && (
                <Button variant="ghost" size="icon-sm" onClick={() => setAddingResource(true)} aria-label="Add resource">
                  <Plus />
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {resources.length === 0 ? (
                <p className="text-sm text-muted-foreground">Slides, repos, and links go here.</p>
              ) : (
                <ul className="space-y-1.5">
                  {resources.map((r) => (
                    <li key={r.id} className="group flex items-center gap-2">
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex min-w-0 items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                      >
                        <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{r.label}</span>
                      </a>
                      {isLead && (
                        <button
                          className="ml-auto shrink-0 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100 focus-visible:opacity-100"
                          aria-label={`Remove ${r.label}`}
                          onClick={async () => {
                            await sb().from('activity_resources').delete().eq('id', r.id)
                            void refetch()
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Users className="h-4 w-4 text-muted-foreground" /> Been through it
                {beenThrough.length > 0 && (
                  <Badge variant="muted" className="ml-auto text-[10px]">
                    {beenThrough.length}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {beenThrough.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nobody yet.{deck && ' Presenting it records who was there.'}
                </p>
              ) : (
                <ul className="space-y-1.5 text-sm">
                  {beenThrough.map((p) => (
                    <li key={p.key} className="flex items-baseline justify-between gap-2">
                      <span className="min-w-0 truncate">
                        {p.name}
                        {p.times > 1 && (
                          <span className="ml-1 text-xs text-muted-foreground">×{p.times}</span>
                        )}
                        {/* Distinguishes "sat through the session" from "ticked
                            the box", which are very different signals when a
                            coach is deciding whether to re-run a deck. */}
                        {!p.attended && (
                          <span className="ml-1.5 text-xs text-muted-foreground">self-marked</span>
                        )}
                      </span>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {formatDateTime(p.when)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {sessions.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <History className="h-4 w-4 text-muted-foreground" /> Sessions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1.5 text-sm">
                  {sessions.map((s) => {
                    const n = perSession.get(s.id) ?? 0
                    return (
                      <li key={s.id} className="flex items-baseline justify-between gap-2">
                        <span className="truncate">{formatDateTime(s.started_at)}</span>
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {s.presenter_id ? nameFor(s.presenter_id) : 'unknown presenter'}
                          {n > 0 && ` · ${n}`}
                        </span>
                      </li>
                    )
                  })}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {editing && (
        <ActivityEditor
          teamId={activity.team_id}
          activity={activity}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false)
            void refetch()
          }}
        />
      )}

      {starting && deck && (
        <PresentStartDialog
          activity={activity}
          onClose={() => setStarting(false)}
          // The session id rides along in the query string so the deck screen
          // can stamp ended_at and show who it recorded.
          onStarted={(sessionId) => navigate(`/activities/${activity.id}/present?session=${sessionId}`)}
        />
      )}

      {addingResource && (
        <ResourceDialog
          activityId={activity.id}
          teamId={activity.team_id}
          onClose={() => setAddingResource(false)}
          onSaved={() => {
            setAddingResource(false)
            void refetch()
          }}
        />
      )}
    </Page>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="min-w-0 truncate text-right font-medium">{children}</span>
    </div>
  )
}

function ResourceDialog({
  activityId,
  teamId,
  onClose,
  onSaved,
}: {
  activityId: string
  teamId: string
  onClose: () => void
  onSaved: () => void
}) {
  const [label, setLabel] = React.useState('')
  const [url, setUrl] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const guard = useDiscardGuard(useDirty({ label, url }), onClose)

  async function save(e: React.FormEvent) {
    e.preventDefault()
    let normalized = url.trim()
    if (!normalized) return setError('Paste a URL.')
    // A pasted "docs.google.com/…" without a scheme would otherwise become a
    // relative link inside the SPA.
    if (!/^https?:\/\//i.test(normalized)) normalized = `https://${normalized}`
    try {
      new URL(normalized)
    } catch {
      return setError('That doesn’t look like a valid URL.')
    }

    setBusy(true)
    setError(null)
    try {
      const { error: err } = await sb()
        .from('activity_resources')
        .insert({ activity_id: activityId, team_id: teamId, label: label.trim() || normalized, url: normalized })
      if (err) throw err
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && guard.requestClose()}>
      <DialogContent className="sm:max-w-md">
        <DiscardPrompt guard={guard} what="this resource" />
        <form onSubmit={save}>
          <DialogHeader>
            <DialogTitle>Add a resource</DialogTitle>
          </DialogHeader>
          <div className="my-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="rlabel">Label</Label>
              <Input id="rlabel" autoFocus value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Slides" />
            </div>
            <Separator />
            <div className="space-y-2">
              <Label htmlFor="rurl">URL</Label>
              <Input id="rurl" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={guard.requestClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              Add
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
